/**
 * **A port of `legacy/frontend/src/screens/SongsScreen.tsx`, whole but for its `:3`.** F-023 T3:
 * «Песни хора», the choir's repertoire — read by everybody, written by the regent — pushed from the
 * choir tab's hub by `ChoirScreen`'s `songs` arm (T10), behind the hub's «Песни хора» tile.
 *
 * Their `:3` is one import mixing the values `ApiError` and `api` with the type `ChoirSongRow` from
 * `../api`. The type is a server shape, so here it comes from `@contracts/index` (ADR-0011) and the
 * line splits in two. Their `:1-2` and their `:4` to the end are theirs byte for byte, and
 * `scripts/ported-partial.txt` holds both regions.
 *
 * Carried as it is, every one `KEEP` (human, 2026-09-21): the composed «Песни хора не пришёл» of
 * `Failed` (`FND-1536`); a refused removal printed in the `Ask` and again on the row behind it
 * (`FND-1538`); a write whose reload fails closing its sheet as on success over the pre-write list
 * (`FND-1539`, «воспроизвести молчание»); the add sheet's draft surviving a close while the edit
 * sheet reseeds (`FND-1540`); the `Ask`'s accessible name emptying while it closes (`FND-1541`);
 * «мая 2026» (`FND-1542`); the ordering promise of «Сверху — …» (`FND-1543`); a date printed twice
 * when it arrives twice (`FND-1544`); `busy` gating the sheets and not the row's three openers
 * (`FND-1545`); dates in the device's zone (`FND-1546`); the add hint naming the regent
 * (`FND-1548`); and no client sort over the server's codepoint order (`FND-1550`).
 *
 * The load's refusal text (their `:52-57`) is ported as written and read by nothing: `Failed` takes
 * no text and `refusal()` is never called with `'screen'` — held by review, `SermonsScreen`'s shape.
 *
 * **C-011 (`FND-1604`, 2026-10-07, after the cutover) cut five holes**: `monthOf` in the format
 * import and the wheel's, the sheet's two pieces of state, the song it is open on, «Добавить дату»
 * first in the panel's actions, and the «Когда пели» sheet before «Изменить песню»'s; a helper at
 * the end.
 * `scripts/ported-partial.txt` holds the regions between them.
 */

import { useCallback, useEffect, useState } from 'react';

import type { ChoirSongRow } from '@contracts/index';
import { ApiError, api } from '../api';
import { datesByYear, formatMonthYear, monthOf } from '../format';
import { ChevronDownIcon, PencilIcon, PlusIcon, TrashIcon } from '../icons';
import { useBackKey } from '../backKey';
import Ask from './Ask';
import Sheet from './Sheet';
import SungDayWheel from './SungDayWheel';
import { Empty, Failed, Settling, Skeleton } from './States';
import Unfold from './Unfold';

/**
 * «Песни хора» — what the choir has learned, and the way one of them becomes a
 * number the whole choir sings.
 *
 * The repertoire and the programme are two different things, which is why they
 * are two tables and two screens: a row here is «мы это поём», a number in a
 * programme is «мы поём это в воскресенье», and one outlives the other by
 * years.
 *
 * Read by everybody and written by the regent. A singer gets the list and
 * nothing else: no button on a row, no capsule, and one line under the title
 * saying whose the list is — the same shape the roster takes for a singer on
 * «Хор».
 */
export default function SongsScreen({
  isRegent,
  onBack,
}: {
  isRegent: boolean;
  onBack: () => void;
}) {
  const [songs, setSongs] = useState<ChoirSongRow[] | null>(null);
  /** The refusal and where it belongs: `screen`, `form`, or one song's id. */
  const [failure, setFailure] = useState<{ at: string | number; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  /** Which song is being asked about, by id. */
  const [removing, setRemoving] = useState<number | null>(null);
  /** Which song the edit sheet is open on, and its two fields seeded from
   *  that row — a sheet that opened empty would read as a second «добавить». */
  const [editing, setEditing] = useState<ChoirSongRow | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editAuthor, setEditAuthor] = useState('');
  /**
   * Whose dates are open, by song id, and null for none. One at a time: two
   * open panels in a list of forty push everything below them twice as far,
   * and the question this screen answers is asked about one song at a time.
   */
  const [opened, setOpened] = useState<number | null>(null);
  /** C-011 (`FND-1604`): which song the «Когда пели» sheet is open on, by id, so the sheet
   *  reads the reloaded row after every write, and the day picked on the wheel. */
  const [dating, setDating] = useState<number | null>(null);
  const [day, setDay] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setSongs(await api.choirSongs());
      setFailure(null);
    } catch (error) {
      setFailure({
        at: 'screen',
        text: error instanceof ApiError ? error.message : 'Не удалось загрузить песни',
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useBackKey(onBack);

  function refusal(at: string | number) {
    return failure !== null && failure.at === at ? <p className="failure">{failure.text}</p> : null;
  }

  async function act(action: () => Promise<unknown>, at: string | number): Promise<boolean> {
    setBusy(true);
    setFailure(null);
    try {
      await action();
      await load();
      return true;
    } catch (error) {
      setFailure({
        at,
        text: error instanceof ApiError ? error.message : 'Не получилось, попробуй ещё раз',
      });
      return false;
    } finally {
      setBusy(false);
    }
  }

  function openEdit(song: ChoirSongRow) {
    setEditing(song);
    setEditTitle(song.title);
    setEditAuthor(song.author ?? '');
  }

  const all = songs ?? [];
  const doomed = all.find((song) => song.song_id === removing) ?? null;
  const dated = all.find((song) => song.song_id === dating) ?? null;
  const trimmed = title.trim();

  return (
    <>
      <div className="screen-head">
        <h1 className="screen-title">Песни хора</h1>
        <p className="screen-caption">что хор разучил</p>
      </div>

      {/* A singer is told whose the list is, in the same place the programme
          says who keeps it. A regent is not: the capsule and the buttons say
          it already, and a line telling him he may edit what he is editing is
          the app repeating itself. */}
      {!isRegent && <p className="hint">Список ведёт регент.</p>}

      {songs === null && failure === null && <Skeleton />}
      {songs === null && failure?.at === 'screen' && (
        <Failed subject="songs" onRetry={() => void load()} />
      )}

      {songs !== null && (
        <Settling>
          <div className="block">
            <span className="label">
              Разучено · <span className="section-count">{all.length}</span>
            </span>
            {/* An order nobody can see is an order nobody trusts: the list runs
                from forgotten to fresh, and the sentence is what makes the
                column of dates read as a ruler rather than as a shuffle. */}
            {all.length > 0 && <p className="hint">Сверху — те, что давно не пели.</p>}

            {all.length === 0 ? (
              <Empty
                fact="Песен пока нет"
                note={
                  isRegent
                    ? 'Добавь то, что хор уже поёт — из этого списка ставятся хоровые номера.'
                    : 'Регент добавит сюда то, что хор разучил.'
                }
              />
            ) : (
              <ul className="live">
                {all.map((song) => {
                  const open = song.song_id === opened;
                  const dates = datesByYear(song.sung_dates);
                  return (
                    <li
                      className={['live-row', 'live-row--stack', open ? 'live-row--dates' : null]
                        .filter((name) => name !== null)
                        .join(' ')}
                      key={song.song_id}
                    >
                      <span className="live-head">
                        <button
                          className="live-tap"
                          data-press="wash"
                          aria-expanded={open}
                          onClick={() => setOpened(open ? null : song.song_id)}
                        >
                          <span className="live-body">
                            <span className="live-name">{song.title}</span>
                            {/* The date first, so every date in the list
                                starts at the same x and the second lines read
                                down the page as a column. Not on the right,
                                where it would cost the title 60px of width -
                                and the right end of this row is empty since
                                2026-09-07, when the mark that stood there went
                                to «Программа» with the action it was for.

                                Every row has this line, the ones with no author
                                included - `author` is nullable on purpose, and a
                                list whose rows are two lines for some songs and
                                one for others has stopped being scannable. */}
                            <span className="live-sub">
                              <span
                                className={
                                  song.last_sung_at === null
                                    ? 'song-when song-when--never'
                                    : 'song-when'
                                }
                              >
                                {song.last_sung_at === null
                                  ? 'ни разу'
                                  : formatMonthYear(song.last_sung_at)}
                              </span>
                              {song.author !== null && (
                                <>
                                  <span className="live-under-sep"> · </span>
                                  {song.author}
                                </>
                              )}
                            </span>
                          </span>
                          <ChevronDownIcon size={18} quiet turned={open} />
                        </button>
                      </span>

                      {/* Dates are something that is merely shown, so they
                          unfold where they stand rather than arriving as a
                          sheet - the rule of three containers, and there is no
                          fourth. */}
                      <Unfold open={open}>
                        <div className="song-dates">
                          <div className="song-dates-head">
                            <span className="label">
                              {song.sung_count === 0 ? (
                                'Ещё не пели'
                              ) : (
                                <>
                                  Когда пели ·{' '}
                                  <span className="section-count">{song.sung_count}</span>
                                </>
                              )}
                            </span>
                          </div>
                          {song.sung_count === 0 ? (
                            <p className="hint">Разучили, но в программе она ещё не стояла.</p>
                          ) : (
                            dates.map((year) => (
                              <p className="song-year" key={year.year}>
                                <span className="song-year-n">{year.year}</span>
                                <span className="song-year-dates">{year.dates}</span>
                              </p>
                            ))
                          )}
                          {/* Two words with their objects, under the dates. The
                              trash stood in the head beside «КОГДА ПЕЛИ» until
                              2026-09-07, and an icon with no word takes its
                              object from whatever is next to it: the owner read
                              it as «убрать эти даты». Removing still asks, and
                              it is not drawn in `--danger` — the cost is stated
                              by the question, and a red word on an otherwise
                              quiet panel would be the only shout on the screen. */}
                          {isRegent && (
                            <div className="song-dates-actions">
                              <button
                                className="song-dates-action"
                                data-press="fade"
                                onClick={() => {
                                  setDating(song.song_id);
                                  setDay(null);
                                }}
                              >
                                <PlusIcon size={15} />
                                Добавить дату
                              </button>
                              <button
                                className="song-dates-action"
                                data-press="fade"
                                onClick={() => openEdit(song)}
                              >
                                <PencilIcon size={15} />
                                Изменить песню
                              </button>
                              <button
                                className="song-dates-action song-dates-action--quiet"
                                data-press="fade"
                                onClick={() => setRemoving(song.song_id)}
                              >
                                <TrashIcon size={15} />
                                Убрать из списка
                              </button>
                            </div>
                          )}
                        </div>
                      </Unfold>
                      {refusal(song.song_id)}
                    </li>
                  );
                })}
              </ul>
            )}

            <p className="hint">
              Эти песни поёт весь хор. Остальные номера хористы записывают сами, и в них видно, кто
              поёт.
            </p>
          </div>
        </Settling>
      )}

      {isRegent && songs !== null && (
        <button className="fab" onClick={() => setAdding(true)}>
          <span className="fab-word">Добавить песню</span>
          <PlusIcon size={14} cropped />
        </button>
      )}

      <Sheet
        open={adding}
        label="Новая песня хора"
        onClose={() => setAdding(false)}
        foot={
          <>
            {refusal('form')}
            <button
              className="btn btn--primary"
              disabled={busy || trimmed === ''}
              onClick={() =>
                void act(
                  () => api.addChoirSong(trimmed, author.trim() === '' ? null : author.trim()),
                  'form',
                ).then((ok) => {
                  if (!ok) return;
                  setAdding(false);
                  setTitle('');
                  setAuthor('');
                })
              }
            >
              Добавить песню
            </button>
          </>
        }
      >
        <label className="field">
          <span>Название</span>
          <input value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)} />
        </label>
        <label className="field">
          <span>Автор или источник</span>
          <input
            value={author}
            maxLength={200}
            onChange={(event) => setAuthor(event.target.value)}
          />
        </label>
        <p className="hint">
          Песни хора ставит в программу регент — тогда номер помечен «хор», и певцов в нём не
          перечисляют.
        </p>
      </Sheet>

      {/* C-011 (`FND-1604`, 2026-10-07): a day the choir sang the song, picked by the regent,
          because the repertoire was entered after years of singing and every song read «ни разу».
          The sheet stays open after a day goes in, so several go in one after another. */}
      <Sheet
        open={dated !== null}
        label="Когда пели"
        onClose={() => setDating(null)}
        foot={
          <>
            {refusal('date')}
            <button
              className="btn btn--primary"
              // Dead on a day already in the list as well: the wheel stays where it was after a day
              // goes in, so the next one is a short scroll away, and a second tap would only be
              // refused with `sung_date_taken`.
              disabled={
                busy ||
                day === null ||
                dated === null ||
                dated.added_dates.some((added) => added.sung_on === day)
              }
              onClick={() => {
                if (dated === null || day === null) return;
                const id = dated.song_id;
                void act(() => api.addSungDate(id, day), 'date');
              }}
            >
              Добавить дату
            </button>
          </>
        }
      >
        <div className="field">
          <span className="when-head">
            День
            {day !== null && <span className="when-value">{dayWords(day)}</span>}
          </span>
          <SungDayWheel value={day} onChange={setDay} />
        </div>
        {dated !== null && dated.added_dates.length > 0 && (
          <>
            <span className="label">Добавлено вручную</span>
            {dated.added_dates.map((added) => (
              <p className="song-year" key={added.date_id}>
                <span className="song-year-dates">{dayWords(added.sung_on)}</span>
                <button
                  className="song-dates-action song-dates-action--quiet"
                  data-press="fade"
                  aria-label={`Убрать дату ${dayWords(added.sung_on)}`}
                  disabled={busy}
                  onClick={() => {
                    const id = dated.song_id;
                    void act(() => api.removeSungDate(id, added.date_id), 'date');
                  }}
                >
                  <TrashIcon size={15} />
                </button>
              </p>
            ))}
          </>
        )}
        <p className="hint">Даты с участий ставятся сами, когда песня стоит в программе.</p>
      </Sheet>

      <Sheet
        open={editing !== null}
        label="Изменить песню"
        onClose={() => setEditing(null)}
        foot={
          <>
            {refusal('edit')}
            {/* The verb in the foot is what tells this sheet from «Новая песня
                хора»: the app's sheets carry no visible heading, and giving
                this one a title to do that job would make it the only sheet
                that has one. */}
            <button
              className="btn btn--primary"
              disabled={busy || editTitle.trim() === ''}
              onClick={() => {
                if (editing === null) return;
                const id = editing.song_id;
                void act(
                  () =>
                    api.updateChoirSong(
                      id,
                      editTitle.trim(),
                      editAuthor.trim() === '' ? null : editAuthor.trim(),
                    ),
                  'edit',
                ).then((ok) => {
                  if (ok) setEditing(null);
                });
              }}
            >
              Сохранить
            </button>
          </>
        }
      >
        <label className="field">
          <span>Название</span>
          <input
            value={editTitle}
            maxLength={200}
            onChange={(event) => setEditTitle(event.target.value)}
          />
        </label>
        <label className="field">
          <span>Автор или источник</span>
          <input
            value={editAuthor}
            maxLength={200}
            onChange={(event) => setEditAuthor(event.target.value)}
          />
        </label>
        {/* The fear this answers is «я испорчу прошлое», and the answer is no:
            a number keeps the title it was sung under, because that is what the
            choir sang. See `ProgramItem.title`, which is a copy for this
            reason. */}
        <p className="hint">
          В прошедших программах останется прежнее название — там записано, как песня называлась в
          тот день.
        </p>
      </Sheet>

      <Ask
        open={doomed !== null}
        title={doomed === null ? '' : `Убрать «${doomed.title}» из списка?`}
        cost={
          <>
            Номера в прошедших программах останутся. Чтобы вернуть песню в список, её придётся
            добавить заново.
          </>
        }
        verb="Убрать песню"
        busy={busy}
        failure={
          doomed !== null && failure !== null && failure.at === doomed.song_id ? failure.text : null
        }
        onConfirm={() => {
          if (doomed === null) return;
          const id = doomed.song_id;
          void act(() => api.removeChoirSong(id), id).then((ok) => {
            if (ok) setRemoving(null);
          });
        }}
        onClose={() => setRemoving(null)}
      />
    </>
  );
}

/** C-011: «12 мая 2024» for a day the server sends as `2024-05-12`, read as a day and never
 *  through `Date`, which would take it for UTC midnight and print the day before in the west. */
function dayWords(on: string): string {
  const [year, month, date] = on.split('-').map(Number);
  return `${date} ${monthOf(month)} ${year}`;
}
